import { useState, useEffect, useCallback, useRef } from "react";
import { Filter, X } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/contexts/AuthContext";

export interface ColumnFilter {
  min?: number;
  max?: number;
}

export interface LeaderboardFilters {
  score: ColumnFilter;
  avgPnl: ColumnFilter;
  avgPnlPerDay: ColumnFilter;
  winRate: ColumnFilter;
  netPnlCaptured: ColumnFilter;
  totalCalls: ColumnFilter;
}

const EMPTY_FILTERS: LeaderboardFilters = {
  score: {},
  avgPnl: {},
  avgPnlPerDay: {},
  winRate: {},
  netPnlCaptured: {},
  totalCalls: {},
};

function loadFilters(storageKey: string): LeaderboardFilters {
  try {
    const raw = localStorage.getItem(storageKey);
    if (raw) {
      const parsed = JSON.parse(raw);
      return { ...EMPTY_FILTERS, ...parsed };
    }
  } catch {}
  return { ...EMPTY_FILTERS };
}

function saveFiltersLocal(storageKey: string, filters: LeaderboardFilters) {
  localStorage.setItem(storageKey, JSON.stringify(filters));
}

function hasAnyFilter(filters: LeaderboardFilters): boolean {
  return Object.values(filters).some(
    (f) => f.min !== undefined || f.max !== undefined
  );
}

export function filterInfluencer(
  entry: { score: number; avgPnl: number; avgPnlPerDay: number; winRate: number; netPnlCaptured: number; totalCalls: number },
  filters: LeaderboardFilters
): boolean {
  for (const [key, filter] of Object.entries(filters) as [keyof LeaderboardFilters, ColumnFilter][]) {
    const val = key === "netPnlCaptured"
      ? entry.netPnlCaptured
      : key === "score"
        ? entry.score
        : entry[key];
    if (filter.min !== undefined && val < filter.min) return false;
    if (filter.max !== undefined && val > filter.max) return false;
  }
  return true;
}

export function useLeaderboardFilters(storageKey: string) {
  const [filters, setFilters] = useState<LeaderboardFilters>(() => loadFilters(storageKey));
  const { user } = useAuth();
  const dbSaveTimer = useRef<ReturnType<typeof setTimeout>>();

  // Load from DB on mount (overrides localStorage if user is logged in)
  useEffect(() => {
    if (!user) return;
    supabase
      .from("user_preferences")
      .select("leaderboard_filters")
      .eq("user_id", user.id)
      .maybeSingle()
      .then(({ data, error }) => {
        if (error || !data?.leaderboard_filters) return;
        const dbFilters = { ...EMPTY_FILTERS, ...(data.leaderboard_filters as Partial<LeaderboardFilters>) };
        setFilters(dbFilters);
        saveFiltersLocal(storageKey, dbFilters);
      });
  }, [user, storageKey]);

  const persistToDb = useCallback(
    (next: LeaderboardFilters) => {
      if (!user) return;
      clearTimeout(dbSaveTimer.current);
      dbSaveTimer.current = setTimeout(() => {
        supabase
          .from("user_preferences")
          .upsert({ user_id: user.id, leaderboard_filters: next, updated_at: new Date().toISOString() })
          .then(() => {})
          .catch(() => {});
      }, 500);
    },
    [user]
  );

  const updateFilter = useCallback(
    (column: keyof LeaderboardFilters, bound: "min" | "max", value: string) => {
      setFilters((prev) => {
        const next = {
          ...prev,
          [column]: {
            ...prev[column],
            [bound]: value === "" ? undefined : Number(value),
          },
        };
        saveFiltersLocal(storageKey, next);
        persistToDb(next);
        return next;
      });
    },
    [storageKey, persistToDb]
  );

  const clearFilters = useCallback(() => {
    const empty = { ...EMPTY_FILTERS };
    setFilters(empty);
    saveFiltersLocal(storageKey, empty);
    persistToDb(empty);
  }, [storageKey, persistToDb]);

  return { filters, updateFilter, clearFilters, hasAnyFilter: hasAnyFilter(filters) };
}

interface FilterRowProps {
  filters: LeaderboardFilters;
  updateFilter: (column: keyof LeaderboardFilters, bound: "min" | "max", value: string) => void;
  clearFilters: () => void;
  hasAnyFilter: boolean;
}

const columns: { key: keyof LeaderboardFilters; label: string; placeholder: string }[] = [
  { key: "score", label: "Score", placeholder: "Score" },
  { key: "avgPnl", label: "Avg PnL", placeholder: "%" },
  { key: "avgPnlPerDay", label: "PnL/day", placeholder: "%" },
  { key: "winRate", label: "Win Rate", placeholder: "%" },
  { key: "netPnlCaptured", label: "Net PnL", placeholder: "%" },
  { key: "totalCalls", label: "Calls", placeholder: "#" },
];

export function LeaderboardFilterRow({ filters, updateFilter, clearFilters, hasAnyFilter }: FilterRowProps) {
  return (
    <div className="hidden md:grid grid-cols-[60px_minmax(160px,1fr)_100px_100px_100px_100px_110px_140px_40px] items-center gap-4 px-5 py-2 border-b border-border bg-muted/30">
      {/* Rank spacer */}
      <div className="flex items-center justify-center">
        <Filter className={`w-3.5 h-3.5 ${hasAnyFilter ? "text-primary" : "text-muted-foreground/50"}`} />
      </div>
      {/* Influencer spacer */}
      <div className="flex items-center">
        {hasAnyFilter && (
          <Button
            variant="ghost"
            size="sm"
            onClick={clearFilters}
            className="h-6 px-2 text-[10px] text-muted-foreground hover:text-foreground gap-1"
          >
            <X className="w-3 h-3" /> Clear filters
          </Button>
        )}
      </div>
      {columns.map((col) => (
        <div key={col.key} className="flex flex-col gap-0.5">
          <Input
            type="number"
            placeholder={`Min`}
            value={filters[col.key].min ?? ""}
            onChange={(e) => updateFilter(col.key, "min", e.target.value)}
            className="h-6 text-[10px] font-mono px-1.5 text-center bg-background/50 [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none"
          />
          <Input
            type="number"
            placeholder={`Max`}
            value={filters[col.key].max ?? ""}
            onChange={(e) => updateFilter(col.key, "max", e.target.value)}
            className="h-6 text-[10px] font-mono px-1.5 text-center bg-background/50 [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none"
          />
        </div>
      ))}
      {/* Arrow spacer */}
      <div />
    </div>
  );
}
